import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { useScopedPath } from "@/lib/scoped";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Copy, ArrowLeft, Trophy, Send, LogOut, Trash2, MessageCircle } from "lucide-react";

const APP_NAME = "Dugsy's Football Challenge";

function copyToClipboard(text, successMsg = "Invite code copied") {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    navigator.clipboard.writeText(text).then(
      () => toast.success(successMsg),
      () => toast.error("Couldn't copy — copy it manually"),
    );
  }
}

function buildInviteMessage(userName, leagueName, inviteCode, joinUrl) {
  const safeName = (userName || "A friend").trim() || "A friend";
  return `${safeName} invites you to join a private league on ${APP_NAME}. Follow the link to ${joinUrl} and enter the code: ${inviteCode}`;
}

function whatsappUrlFor(userName, leagueName, inviteCode, joinUrl) {
  return `https://wa.me/?text=${encodeURIComponent(buildInviteMessage(userName, leagueName, inviteCode, joinUrl))}`;
}

export default function LeagueDetailPage() {
  const scope = useScopedPath();
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Shareable invite URL — slug-aware so links dropped in WhatsApp / SMS
  // send the recipient back to the same branded environment the sender is in.
  const joinUrl = (typeof window !== "undefined" ? window.location.origin : "") + scope("/leagues");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, lb] = await Promise.all([
        api.get(`/leagues/${id}`),
        api.get(`/leagues/${id}/leaderboard`),
      ]);
      setLeague(l.data);
      setRows(Array.isArray(lb.data) ? lb.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const leave = async () => {
    if (!league || !window.confirm(`Leave "${league.name}"?`)) return;
    setBusy(true);
    try {
      await api.post(`/leagues/${league.id}/leave`);
      toast.success(`Left "${league.name}"`);
      navigate(scope("/leagues"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!league || !window.confirm(`Delete "${league.name}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/leagues/${league.id}`);
      toast.success(`Deleted "${league.name}"`);
      navigate(scope("/leagues"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</p>
        </main>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="border border-dashed border-zinc-300 rounded-sm p-12 text-center" data-testid="league-not-found">
            <p className="font-heading text-2xl font-semibold text-zinc-950">League not found.</p>
            <Link to={scope("/leagues")} className="inline-block mt-4 text-sm font-semibold text-[#0038FF] underline">Back to my leagues</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to={scope("/leagues")} className="inline-flex items-center text-xs text-zinc-500 hover:text-zinc-950 mb-6" data-testid="league-back-link">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> All leagues
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 inline-flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> Private league
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mt-1" data-testid="league-name">{league.name}</h1>
            <p className="text-sm text-zinc-500 mt-2">
              <span data-testid="league-member-count">
                {league.member_count}/{league.member_cap || 20}
              </span>{" "}
              {league.member_count === 1 ? "member" : "members"} · run by {league.owner_name}
              {league.is_full && (
                <span data-testid="league-full-badge" className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm bg-rose-50 text-rose-700 text-[10px] font-bold uppercase tracking-[0.15em] border border-rose-200">
                  Full
                </span>
              )}
            </p>
            <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-2" data-testid="league-today-label">
              <span className="font-semibold text-zinc-950 tabular-nums">{new Date().toLocaleDateString("en-GB")}</span>
            </p>
          </div>
          <div className="border border-zinc-200 rounded-sm p-4 bg-zinc-50">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Invite code</p>
            <div className="mt-1 inline-flex items-center gap-2 flex-wrap">
              <code data-testid="league-invite-code" className="font-mono text-lg tracking-widest text-zinc-950">{league.invite_code}</code>
              <button
                type="button"
                onClick={() => copyToClipboard(league.invite_code, "Invite code copied")}
                data-testid="league-copy-btn"
                className="text-xs font-semibold text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
              {league.is_owner && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      copyToClipboard(
                        buildInviteMessage(
                          user?.name || user?.email || "A friend",
                          league.name,
                          league.invite_code,
                          joinUrl,
                        ),
                        "Invite message copied — paste it into WhatsApp, text, email…",
                      )
                    }
                    data-testid="league-copy-invite-btn"
                    className="text-xs font-semibold text-[#0038FF] hover:text-blue-800 inline-flex items-center gap-1"
                  >
                    <Send className="h-3 w-3" /> Copy invite
                  </button>
                  <a
                    href={whatsappUrlFor(user?.name || user?.email || "A friend", league.name, league.invite_code, joinUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="league-whatsapp-invite-btn"
                    className="text-xs font-semibold text-[#25D366] hover:text-emerald-700 inline-flex items-center gap-1"
                  >
                    <MessageCircle className="h-3 w-3" /> WhatsApp
                  </a>
                </>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              {league.is_owner ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={remove}
                  disabled={busy}
                  data-testid="league-delete-btn"
                  className="h-9 rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 text-xs"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete league
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={leave}
                  disabled={busy}
                  data-testid="league-leave-btn"
                  className="h-9 rounded-sm border-zinc-300 text-zinc-700 hover:bg-zinc-50 text-xs"
                >
                  <LogOut className="h-3.5 w-3.5 mr-1" /> Leave league
                </Button>
              )}
            </div>
          </div>
        </div>

        <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mb-4 inline-flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-600" /> Standings
        </h2>

        {rows.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-10 text-center" data-testid="league-empty-standings">
            <p className="text-sm text-zinc-500">No members yet. Share the invite code above to get started.</p>
          </div>
        ) : (
          <div className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="league-leaderboard">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 w-16">#</th>
                  <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Player</th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Matches</th>
                  <th
                    className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"
                    title="Number of individual stat predictions hit at the max (3 pts on goals/shots/corners/offsides/fouls, 2 pts on throw-ins). Used as the 3rd tie-break."
                  >
                    Correct
                  </th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Total points</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isMe = user && r.user_id === user.id;
                  return (
                    <tr
                      key={r.user_id || `${r.user_name}-${i}`}
                      data-testid={`league-row-rank-${i + 1}`}
                      className={`border-b border-zinc-100 last:border-b-0 ${isMe ? "bg-[#0038FF]/5" : ""}`}
                    >
                      <td className="px-6 py-4 font-heading font-bold text-zinc-950 tabular-nums">
                        {i === 0 ? <span className="text-[#0038FF]">{i + 1}</span> : i + 1}
                      </td>
                      <td className="px-6 py-4 font-medium text-zinc-950">
                        {r.user_name}{isMe ? " (you)" : ""}
                      </td>
                      <td className="px-6 py-4 text-right text-zinc-700 tabular-nums">{r.matches_played}</td>
                      <td
                        className="px-6 py-4 text-right text-zinc-700 tabular-nums"
                        data-testid={`league-row-correct-${i + 1}`}
                      >
                        {r.correct_count ?? 0}
                      </td>
                      <td className="px-6 py-4 text-right font-heading font-bold text-2xl text-zinc-950 tabular-nums">
                        {r.total_points}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-8 text-xs text-zinc-500 uppercase tracking-[0.15em]">
          Private league · standings drawn from the current season only
        </p>
      </main>
    </div>
  );
}
